import * as React from 'react'
import { IContextProvider } from "../../../uxp";
import classNames from 'classnames';
import { IContextData } from '../../../hooks/useContextData';
import { convertColorStyles, hasValue, parseValue, replaceWithData } from '../../../util';
import { hoursToMilliseconds } from 'date-fns';
import { Tooltip } from 'uxp/components';
import './HeatMapUI.scss';

const DAYS_OF_WEEK = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTHS_OF_YEAR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sept','Oct','Nov','Dec'];
const HOURS_OF_DAY = Array.from({length:24},(_,i)=>i+':00');

export declare type IHeatMapCellStyle = 'square' | 'circle';
export declare type IHeatMapAxisType = 'dayofweek'|'month'|'hour'|'field'|'custom';
export declare type IHeatMapReducer = (items:any[])=>number;
export declare type IHeatMapData = {[key:string]:{[subKey:string]:{[xKey:string]:any[]}}};

export interface IHeatMapAxis {
    type: IHeatMapAxisType;
    values?:Array<{label:string,value:string}>;
    field?:string;
    showSummary?: boolean;
}
export interface IHeatMapUIProps {
    uxpContext:IContextProvider;
    contextData?:IContextData;
    data:any[];
    
    xAxis:IHeatMapAxis;
    yAxis:IHeatMapAxis;
    yAxisSecondary?:IHeatMapAxis;
    subYAxisEnabled: boolean;
    valueField: string;
    aggregation:'min' | 'max' | 'sum' |'count'|'average'|IHeatMapReducer;
    showCellLabels?: boolean;
    cellFormatter?:(value:number)=>string;
    xAxisBackground?: string;
    xAxisForeground?: string;

    yAxisBackground?: string;
    yAxisForeground?: string;

    subYAxisBackground?: string;
    subYAxisForeground?: string;


    cellBorder?: boolean;
    cellBorderColor?: string;
    cellStyle?:IHeatMapCellStyle;
    cellColors?:Array<{stopAt:number,color:string}>;
    emptyCellColor?: string;
    backgroundColor?: string;
}
function parseDate(d:any) {
    if (!d) {
        return null;
    }
    if (d instanceof Date) return d;
    return new Date(d.toString());
}
function dayName(d?:Date) {
    if (!d) return '??';
    return  DAYS_OF_WEEK[d.getDay()];
}
function monthName(d?:Date) {
    if (!d) return '??';
    return  MONTHS_OF_YEAR[d.getMonth()];
}
function transformValueForAxis(type:IHeatMapAxisType,value:any) {
    switch(type) {
        default: 
        case 'custom':return value;
        case 'field': return value;
        case 'dayofweek': return dayName(parseDate(value));
        case 'month': return monthName(parseDate(value));
        case 'hour': return (parseDate(value)?.getHours?.()) + ':00';
    }
}
function getLabelsForAxis(axis:IHeatMapAxis,items:Set<string>):any[] {
    switch(axis.type) {
        case 'dayofweek': return DAYS_OF_WEEK;
        case 'hour': return  HOURS_OF_DAY;
        case 'month': return MONTHS_OF_YEAR;
        case 'custom': return axis?.values || [];
        case 'field':{
            return [...items];
          
        }
    }

}
export const HeatMapUI:React.FC<IHeatMapUIProps> = (props)=>{
    const {data,xAxis,yAxis,contextData,valueField,subYAxisEnabled,yAxisSecondary} = props;
    const {aggregation} = props;
    let [readings, setReadings] = React.useState<any[]>([]);
    let [expanded, setExpanded] = React.useState<string[]>([]);

    let reducer:IHeatMapReducer;

    function handleExpansion(hasChildren: boolean, name: string) {

        let _expanded = [...expanded]
        if (hasChildren) {
            if (_expanded.indexOf(name) == -1) {
                // if not add 
                _expanded.push(name)
            }
            else {
                _expanded.splice(_expanded.indexOf(name), 1)
            }
        }
        setExpanded(_expanded)


    }

    let colorValues = props.cellColors.length ? props.cellColors : [{stopAt:Number.MAX_SAFE_INTEGER,color:'#0066FF'}];
    colorValues.sort((a,b)=>a.stopAt  - b.stopAt);
    function getCellColor(value?:number) {
        if (value==null || typeof value == 'undefined'||isNaN(value)) {
            return props.emptyCellColor ||'';
        }
        for(let i=0;i<colorValues.length;i++) {
            let cv =colorValues[i].stopAt;
            if (value <=cv) return colorValues[i].color;
        }
        return colorValues[colorValues.length-1].color;

    }
    function formatValue(value:number,force?:boolean) {
        if (!props.showCellLabels && force!=true) {
            return '';
        }
        if (props.cellFormatter) {
            var x =  props.cellFormatter(value);
            return x;
        }
        return Number(value).toFixed(2);
    }
    

    if (typeof aggregation == 'function') {
        reducer = aggregation;
    } else {
        switch(aggregation) {
            default:
            case 'average': 
                reducer = (items)=>items ? items.reduce((p,a)=>p+Number(a))/(items.length):0;
                break;
            case 'min': 
                reducer = (items) => items.reduce((p,a)=>Math.min(p,a),Number.MAX_SAFE_INTEGER);
                break;
            case 'max': 
                reducer = (items) => items.reduce((p,a)=>Math.max(p,a),Number.MIN_SAFE_INTEGER);
                break;
            case 'count': 
                reducer = (items) => items.length;
                break;
            case 'sum': 
                reducer =(items) => items.reduce((p,a)=>p+Number(a),0);
                break;
        }
    }


    
    const allYLabels = new Set<string>([]);
    const allYSecondLables = new Set<string>([]);
    const allXLabels = new Set<string>([]);

    const heatMapData:IHeatMapData = {};
    for(let i=0;i<data.length;i++) {
        const row = data[i];

        let yLabelMain = '';
        yLabelMain = transformValueForAxis(yAxis.type, replaceWithData(yAxis.field,{...contextData,row}));
       
        let yLabelSecondary = '';
        if (subYAxisEnabled && yAxisSecondary) {
            yLabelSecondary = transformValueForAxis(yAxisSecondary.type,replaceWithData(yAxisSecondary.field,{...contextData,row}));
        }
        
        const xLabel = transformValueForAxis(xAxis.type,replaceWithData(xAxis.field,{...contextData,row}));

        if (!heatMapData[yLabelMain]) {
            heatMapData[yLabelMain] = {};
        }
        if (!heatMapData[yLabelMain][yLabelSecondary]) {
            heatMapData[yLabelMain][yLabelSecondary] = {};
        }
        if (!heatMapData[yLabelMain][yLabelSecondary][xLabel]) {
            heatMapData[yLabelMain][yLabelSecondary][xLabel] = [];
        }
        const value = (replaceWithData(valueField,{...contextData,row}));
        heatMapData[yLabelMain][yLabelSecondary][xLabel].push(value);
        allYLabels.add(yLabelMain);
        allXLabels.add(xLabel);
        allYSecondLables.add(yLabelSecondary);
    }

    let xAxisLabels = getLabelsForAxis(xAxis,allXLabels);
    let yAxisLabels = getLabelsForAxis(yAxis,allYLabels);

   
    return <div className="heatmap-table" style={{backgroundColor:props.backgroundColor || undefined}}>
    <table>
        <tr className="hm-header" >
            <td></td>
            {
                xAxisLabels.map(x =>
                    <td key={x} >
                        <div style={{
                        border:props.cellBorder ? `1px solid ${props.cellBorder}`:undefined,
                        backgroundColor: props.xAxisBackground || undefined,
                        color: props.xAxisForeground || undefined,
                        }} >{x}</div>
                    </td>)
            }

            {xAxis.showSummary && <td><div >Maximum</div></td>}
        </tr>

        <tr className="hm-spacer" >
            <td colSpan={xAxisLabels.length+1}></td>
        </tr>
        {
            yAxisLabels.map((lbl)=>{
                let items = heatMapData[lbl]||{};
                let subItems = Object.keys(items);
              
                const hasChildren = subYAxisEnabled;
                let _expanded = expanded.indexOf(lbl) !== -1
                const yValues:any = {};
             

                return <>
                <tr key={lbl}>
                        <td className={classNames('hm-cell-name', { 'has-children': hasChildren, 'expanded': _expanded })}
                            onClick={() => handleExpansion(hasChildren, lbl)}
                        >
                            <div style={{
                        border:props.cellBorder ? `1px solid ${props.cellBorder}`:undefined,
                        backgroundColor: props.yAxisBackground || undefined,
                        color: props.yAxisForeground || undefined,
                        }} >{lbl}</div>
                        </td>
                        {
                            xAxisLabels.map(x => {
                                let items:any[] = [];
                                for (let item of subItems) {
                                    items = [...items, ...(heatMapData[lbl][item]?.[x] || [])];
                                }
                                let value = items.length ?  reducer(items):null;

                                
                                return <td key={x} style={{border:props.cellBorder ? `1px solid ${props.cellBorderColor}`:undefined}}>
                                    <Tooltip content={() => <div className='hm-tt'>
                                        <div>{lbl}</div>
                                        <div className='actual-time'>{formatValue(value,true)}</div>
                                    </div>}>
                                    <div style={{
                                        ...(props.cellStyle=='circle' ? 
                                            {
                                                borderRadius:'50%', 
                                                marginLeft:'auto',
                                                marginRight:'auto',
                                                marginTop:'auto',
                                                marginBottom:'auto',
                                                width:'20px',
                                                height:'20px'
                                            } : {}
                                        ),
                                        backgroundColor: getCellColor(value)
                                         }} >{formatValue(value)} </div>

                                    </Tooltip>
                                </td>
                            })
                        }
                    </tr>
                    {
                        hasChildren && _expanded && subItems.map((lbl2,k)=>{
                            let _class = ''
                            if (k == 0) _class += ' has-top-border'
                            if (k == (subItems.length - 1)) _class += ' has-bottom-border'
    
                            return <tr key={`${lbl2}-${k}`} className={classNames("child-row", _class)}>
                                <td className={classNames('hm-cell-name child-row')}
                                >
                                    <div style={{   border:props.cellBorder ? `1px solid ${props.cellBorder}`:undefined,
                        backgroundColor: props.subYAxisBackground || undefined,
                        color: props.subYAxisForeground || undefined,}}>{lbl2}</div>
                                </td>
                                {
                                    xAxisLabels.map(x => {

                                        let values = heatMapData?.[lbl]?.[lbl2]?.[x]||[];
                                        let value = values.length ?  reducer(values) : null;
    
                                       
                                        return <td key={x}>
                                               <Tooltip content={() => <div className='hm-tt'>
                                        <div>{lbl}</div>
                                        <div>{lbl2}</div>
                                        <div className='actual-time'>{formatValue(value,true)}</div>
                                    </div>}>
                                    <div style={{
                                        ...(props.cellStyle=='circle' ? 
                                            {
                                                borderRadius:'50%', 
                                                marginLeft:'auto',
                                                marginRight:'auto',
                                                marginTop:'auto',
                                                marginBottom:'auto',
                                                width:'20px',
                                                height:'20px'
                                            } : {}
                                        ),
                                        backgroundColor: getCellColor(value)
                                         }} >{formatValue(value)} </div>

                                    </Tooltip>
                                      
                                        </td>
                                    })
                                }
                            </tr>;
                        })
                    }
                </>;
            })
        }
        

    </table>
    </div>;
}

let _sampleData:any[] = null;
export function generateSampleHeatMapData() {
    if (_sampleData) {
        return _sampleData;
    }
    let data: any[] = [];
    let locations: any = {
        'Meeting Room': ['A',],
        'Lounge': ['L1', 'L2',],
        'Cubicle': [...Array.from({ length: 2 }, (x, e) => 'Space ' + e)]
    };
    let types = Object.keys(locations);
    for (let type of types) {
        let rooms = locations[type];
        for (let room of rooms) {
            let d = new Date();
            for (let i = 0; i < 1; i++) {
                for (let h = 8; h < 19; h++) {

                    let nd = new Date();
                    nd.setDate(nd.getDate() - h);
                    nd.setHours(h);
                    let value = Math.random() * 3;
                    data.push({ locationType: type, location: room, value, time: nd.toISOString() });
                }
            }
        }
    }
    _sampleData = data;
    return _sampleData;
}